/**
 * Resolve page metadata for a pathname + search string.
 */

import {
  APP_NAME,
  DEFAULT_PRIVATE_DESCRIPTION,
  DEFAULT_PUBLIC_DESCRIPTION,
  DEFAULT_PUBLIC_TITLE,
  PAGE_META_CATALOG,
} from './catalog';
import type { PageMeta, PageMetaEntry, PageMetaOverride } from './types';

/**
 * Prefix match that does not treat `/account` as matching `/accountancy`.
 * Entries whose path already ends with `/` (public capture URLs) keep startsWith.
 */
function pathMatchesSafe(entry: PageMetaEntry, pathname: string): boolean {
  if (entry.exact) return pathname === entry.path;
  if (entry.path.endsWith('/')) return pathname.startsWith(entry.path);
  return pathname === entry.path || pathname.startsWith(`${entry.path}/`);
}

function tabFromSearch(search: string): string | null {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return null;
  return new URLSearchParams(raw).get('tab');
}

/**
 * Default `?tab=` when the hub URL omits it. Studio omits a default so the
 * section title stays "Automations" until an explicit tab is selected.
 */
const DEFAULT_TAB_BY_PATH: Record<string, string> = {
  '/account': 'profile',
  '/business': 'overview',
};

function entryMatches(entry: PageMetaEntry, pathname: string, tab: string | null): boolean {
  if (!pathMatchesSafe(entry, pathname)) return false;

  if (entry.tab !== undefined) {
    // Tabbed hubs only match the hub root — never nested routes like /studio/flow/:id.
    if (pathname !== entry.path) return false;
    const effectiveTab = tab ?? DEFAULT_TAB_BY_PATH[entry.path] ?? null;
    return effectiveTab === entry.tab;
  }

  return true;
}

function fromEntry(entry: PageMetaEntry): PageMeta {
  return {
    title: entry.title,
    description: entry.description,
    indexable: entry.indexable === true,
    canonicalPath: entry.indexable === true ? entry.canonicalPath : undefined,
  };
}

/** Lookup catalog metadata; never returns empty title/description. */
export function resolvePageMeta(pathname: string, search = ''): PageMeta {
  const tab = tabFromSearch(search);
  const match = PAGE_META_CATALOG.find((entry) => entryMatches(entry, pathname, tab));
  if (match) return fromEntry(match);

  if (pathname === '/' || pathname === '/landing') {
    return {
      title: DEFAULT_PUBLIC_TITLE,
      description: DEFAULT_PUBLIC_DESCRIPTION,
      indexable: true,
      canonicalPath: '/',
    };
  }

  return {
    title: APP_NAME,
    description: DEFAULT_PRIVATE_DESCRIPTION,
    indexable: false,
  };
}

/** Merge a page-level override onto resolved route metadata. */
export function mergePageMeta(
  base: PageMeta,
  override: PageMetaOverride | null | undefined,
): PageMeta {
  if (!override) return base;
  const title = override.title?.trim() || base.title;
  const description = override.description?.trim() || base.description;
  const indexable = override.indexable ?? base.indexable;
  const canonicalPath = indexable
    ? (override.canonicalPath ?? base.canonicalPath)
    : undefined;
  return {
    title: title || APP_NAME,
    description: description || DEFAULT_PRIVATE_DESCRIPTION,
    indexable,
    canonicalPath,
  };
}

/**
 * Window/tab document title: "Page — BuzzBit X", with optional unread prefix.
 * Titles that already include the brand name are left as-is.
 */
export function documentTitleFor(
  pathname: string,
  unread = 0,
  search = '',
  override?: PageMetaOverride | null,
): string {
  const meta = mergePageMeta(resolvePageMeta(pathname, search), override);
  const pageTitle = meta.title;
  const base =
    pageTitle === APP_NAME ||
    pageTitle === DEFAULT_PUBLIC_TITLE ||
    pageTitle.includes(APP_NAME)
      ? pageTitle
      : `${pageTitle} — ${APP_NAME}`;
  if (unread <= 0) return base;
  return `(${unread > 99 ? '99+' : unread}) ${base}`;
}

/** In-app heading (TopBar): short page name, never empty. */
export function resolveRouteTitle(pathname: string, search = ''): string {
  const meta = resolvePageMeta(pathname, search);
  if (meta.title === DEFAULT_PUBLIC_TITLE) return APP_NAME;
  return meta.title || APP_NAME;
}
